import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ProductoList } from '@app/entidades/operacion.model';
import { FiltroProductos, ProductoDTO } from '@app/entidades/producto.model';
import { ProductoService } from '@app/servicios/producto/producto.service';
import { StorageService } from '@app/servicios/storage/storage.service';
import { environment } from '@env';
import { Subscription, timer } from 'rxjs';

@Component({
  selector: 'app-producto',
  templateUrl: './producto.component.html',
})
export class ProductoComponent implements OnDestroy {
  fotoUrl = environment.servImgURL + '/';

  public selectedFotoId?: number;

  public urlFoto: string = this.fotoUrl + '32579';

  private subscriptions: Subscription = new Subscription();
  public id: string = '';
  public producto!: ProductoDTO;
  public lista: ProductoList[] = [];
  public cantidad: number = 1;
  public alertOk = false;

  public description: string[] = [];
  productos: ProductoDTO[] = [];

  mobile: boolean = false;
  web: boolean = false;
  maxStock: boolean = false;
  showStickyBar: boolean = false;

  @HostListener('window:resize', ['$event'])
  onWindowWidth() {
    if (window.innerWidth < 1023) {
      this.web = false;
      this.mobile = true;
    } else {
      this.mobile = false;
      this.web = true;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.showStickyBar = scrollOffset > 380;
  }

  constructor(
    private productoServ: ProductoService,
    private _route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService,
    private location: Location
  ) {}

  ngOnInit() {
    this.onWindowWidth();
    this._route.paramMap.subscribe((params: ParamMap) => {
      this.id = params.get('id');
      this.obtenerProducto();
    });
  }

  goBack() {
    this.location.back();
  }

  public obtenerProducto() {
    this.subscriptions.add(
      this.productoServ.producto(this.id).subscribe((resp) => {
        this.producto = resp.data;
        this.verificarCantidad();
        this.agregarSaltos();
        this.urlFoto = this.fotoUrl + this.producto.foto;
        this.initSelectedFoto();

        this.consultarProductos();
      })
    );
  }

  private initSelectedFoto() {
    const principal = this.producto?.foto ?? 0;
    this.selectedFotoId = principal || this.producto?.galeria?.[0] || 0;
    this.urlFoto = this.fotoUrl + this.selectedFotoId;
  }

  selectFoto(id: number) {
    if (!id || id === this.selectedFotoId) return;
    this.selectedFotoId = id;
    this.urlFoto = this.fotoUrl + id;
  }

  verificarCantidad() {
    this.lista = this.storageService.getItem('lp') || [];

    this.lista.forEach((producto) => {
      if (producto.id === this.producto?.id) {
        this.cantidad = producto.cantidad;
      }
    });
  }

  agregarProducto() {
    if (!this.producto || !this.producto.stock) return;

    const maxAvailable = Number(this.producto.stock.stock ?? 0);
    const localStock = Number(this.producto.stock.local ?? 0);
    if (!Number.isFinite(maxAvailable) || maxAvailable <= 0 || !Number.isFinite(localStock) || localStock <= 0) {
      this.maxStock = true;
      return;
    }

    let qty = Number(this.cantidad);
    if (!Number.isFinite(qty) || qty < 1) qty = 1;
    qty = Math.floor(qty);

    if (qty > maxAvailable) {
      qty = maxAvailable;
      this.maxStock = true;
    }

    this.lista = this.storageService.getItem('lp') || [];

    const existing = this.lista.find((p) => p.id === this.producto.id);
    if (existing) {
      const nueva = Math.min(existing.cantidad + qty, maxAvailable);
      if (nueva < 1) return;
      existing.cantidad = nueva;
    } else {
      let prod: ProductoList = {
        id: this.producto.id,
        nombre: this.producto.nombre,
        foto: this.urlFoto,
        cantidad: qty,
        stock: maxAvailable,
        tipo: this.producto.tipo,
        precio: this.producto.precios,
      };

      this.lista.push(prod);
    }

    this.alertSuccess();
    this.storageService.setItem('lp', this.lista);
  }

  sumar() {
    const maxAvailable = Number(this.producto?.stock?.stock ?? 0);
    if (!Number.isFinite(maxAvailable) || maxAvailable <= 0) {
      this.maxStock = true;
      return;
    }

    let current = Number(this.cantidad) || 0;
    if (current < 1) current = 1;

    if (current >= maxAvailable) {
      this.cantidad = maxAvailable;
      this.maxStock = true;
      return;
    }

    this.cantidad = current + 1;
    this.maxStock = this.cantidad >= maxAvailable;
  }

  restar() {
    let current = Number(this.cantidad) || 1;
    const next = Math.max(current - 1, 1);
    this.cantidad = next;

    const maxAvailable = Number(this.producto?.stock?.stock ?? 0);
    this.maxStock = Number.isFinite(maxAvailable) && maxAvailable > 0 && this.cantidad >= maxAvailable;
  }

  agregarSaltos() {
    if (!this.producto?.descripcion) {
      this.description = [];
      return;
    }
    if (this.producto.descripcion.includes(':;')) {
      this.description = this.producto.descripcion.split(':;');
    } else {
      this.description = this.producto.descripcion.split('\n');
    }
  }

  consultarProductos() {
    const filter: FiltroProductos = {
      activo: 'ACTIVO',
      id: '',
      nombre: '',
      categoria: this.producto?.categoria,
      tipo: this.producto?.tipo,
      subtipo: this.producto?.subtipo,
      codPrecio: '',
      esNuevo: '',
      visible: true,
    };

    const page = 0;
    const size = 20;

    this.subscriptions.add(
      this.productoServ
        .filtrarProductos(page, size, filter)
        .subscribe((resp) => {
          this.productos = resp.data;
        })
    );
  }

  alertSuccess() {
    this.alertOk = true;
    timer(1500).subscribe((N) => (this.alertOk = false));
  }

  get subtotal(): number {
    const precio = Number(this.producto?.precios?.publico ?? 0);
    const cant = Number(this.cantidad ?? 1);
    return precio * cant;
  }

  get stockDisponible(): number {
    return Number(this.producto?.stock?.stock ?? 0);
  }

  get isDisponible(): boolean {
    return (this.producto?.stock?.stock ?? 0) > 0 && (this.producto?.stock?.local ?? 0) > 0;
  }

  get isPorIngresar(): boolean {
    return (this.producto?.stock?.stock ?? 0) > 0 && (this.producto?.stock?.local ?? 0) <= 0;
  }

  get isSinStock(): boolean {
    return !this.producto?.stock || (this.producto?.stock?.stock ?? 0) <= 0;
  }

  get whatsAppUrl(): string {
    const nombre = this.producto?.nombre || 'este producto';
    const text = `Hola! Quiero consultar por el producto "${nombre}" que vi en la tienda web.`;
    return `https://wa.me/5491167427845?text=${encodeURIComponent(text)}`;
  }

  onCantidadInput(event: any) {
    let val = parseInt(event.target.value, 10);
    const maxAvailable = this.stockDisponible;
    if (isNaN(val) || val < 1) {
      val = 1;
    }
    if (maxAvailable > 0 && val > maxAvailable) {
      val = maxAvailable;
      this.maxStock = true;
    } else {
      this.maxStock = false;
    }
    this.cantidad = val;
  }

  comprarAhora() {
    this.agregarProducto();
    this.router.navigate(['/finalizar']);
  }

  fotoSiguiente() {
    const list = this.producto?.galeria?.length ? this.producto.galeria : [this.producto?.foto];
    if (!list || list.length <= 1) return;
    const currentIndex = list.indexOf(this.selectedFotoId || 0);
    const nextIndex = (currentIndex + 1) % list.length;
    this.selectFoto(list[nextIndex]);
  }

  fotoAnterior() {
    const list = this.producto?.galeria?.length ? this.producto.galeria : [this.producto?.foto];
    if (!list || list.length <= 1) return;
    const currentIndex = list.indexOf(this.selectedFotoId || 0);
    const prevIndex = (currentIndex - 1 + list.length) % list.length;
    this.selectFoto(list[prevIndex]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
